import { DayObject, TimeOfDay } from "@/types/date"
import { ItineraryEvent, ItineraryItem } from "@/types/itinerary"
import { Place } from "@/types/place"
import { WebSearchResult } from "@/types/web-search-result"
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit"

import { assertUnreachable } from "@/lib/utils/assertions"
import { RootState } from "./redux-store"

export interface DraggingSliceState {
  draggingItem: DraggableItem | undefined
}
export type DraggableItem =
  | DraggablePlace
  | DraggableItineraryItem
  | DraggableEntityTitle
  | DraggableSearchResult

// Dragging a place that's outside the itinerary (like from the chat)
export interface DraggablePlace {
  type: "place"
  place: Place
  originalEvent?: ItineraryEvent // For unscheduled events being dragged to schedule
  hasFixedSchedule?: boolean
}
// Dragging an item in the itinerary.
export interface DraggableItineraryItem {
  type: "itinerary-item"
  item: ItineraryItem
  allDay: boolean
  startDay: DayObject // day being dragged from
  startTimeOfDay: TimeOfDay
  startCoordinateY: number // starting coordinate relative to the day div
  hasFixedSchedule?: boolean
}
export interface DraggableEntityTitle {
  type: "entity-title"
  title: string
  defaultTimeZone: string
}
export interface DraggableSearchResult {
  type: "search-result"
  searchResult: WebSearchResult
  messageId?: number
  webSearchResultId?: string
  defaultTimeZone: string
  hasFixedSchedule?: boolean
}

export const draggingSliceInitialState: DraggingSliceState = {
  draggingItem: undefined,
}

const selectSliceState = (state: RootState) => state.draggingSlice

export const selectDraggingItem = createSelector(
  [selectSliceState],
  (state) => state.draggingItem
)

export const selectIsDraggingItem = createSelector(
  [selectDraggingItem],
  (item) => !!item
)

export const selectDraggingEntityRef = createSelector(
  [selectDraggingItem],
  (item) => item?.type === "place" && item.place.entity_ref
)

export const draggingSlice = createSlice({
  name: "dragging",
  initialState: draggingSliceInitialState,
  reducers: {
    setDraggingItem: (
      state: DraggingSliceState,
      action: PayloadAction<DraggableItem>
    ) => {
      state.draggingItem = action.payload
    },
    clearDraggingItem: (state: DraggingSliceState) => {
      state.draggingItem = undefined
    },
  },
})

//
//  Helpers
//

export function timeZoneFromDraggableItem(draggable: DraggableItem): string {
  const type = draggable.type
  switch (type) {
    case "place":
      return draggable.place.time_zone
    case "search-result":
      if (draggable.searchResult.type === "event") {
        return (
          draggable.searchResult.schedule.time_zone || draggable.defaultTimeZone
        )
      } else {
        return draggable.defaultTimeZone
      }
    case "itinerary-item":
      return draggable.item.timeZone
    case "entity-title":
      // TODO: in this case, we may actually want the drop area to be
      // smart about setting the time zone for this entity title.
      return draggable.defaultTimeZone
    default:
      assertUnreachable(type)
  }
}
export function eventFromDraggableItem(
  draggable: DraggableItem
): ItineraryEvent | undefined {
  const type = draggable.type
  switch (type) {
    case "place":
    case "entity-title":
    case "search-result":
      return undefined
    case "itinerary-item":
      return draggable.item.event
    default:
      assertUnreachable(type)
  }
}

export const { setDraggingItem, clearDraggingItem } = draggingSlice.actions
export default draggingSlice.reducer
